'''<script setup>
import { ref, watch, nextTick } from 'vue'
import OutlineButton from '../buttons/OutlineButton.vue'

const props = defineProps({
    upperTitle: {
        type: String,
        default: 'L’application révolutionnaire'
    },
    title: {
        type: String,
        default: 'Un agent IA dédié aux commissaires de justice'
    },
    buttonLabel: {
        type: String,
        default: 'Découvrir'
    },
    heroFirstVideoSrc: {
        type: String,
        default: '/videos/inside.mp4'
    },
    heroSecondVideoSrc: {
        type: String,
        default: '/videos/discover-orion.mp4'
    },
    heroFirstVideoPoster: {
        type: String,
        default: '/images/home-video-placholder.webp'
    },
    heroSecondVideoPoster: {
        type: String,
        default: '/images/image-placeholder.svg'
    }
})

const isExpanded = ref(false)
const videoPlayer = ref(null)

const openVideo = () => {
  isExpanded.value = true
}

const closeVideo = () => {
  isExpanded.value = false
}

watch(isExpanded, async (newVal) => {
  await nextTick();
  if (newVal) {
    if (videoPlayer.value) {
      videoPlayer.value.play()
    }
  } else {
    if (videoPlayer.value) {
      videoPlayer.value.pause()
      videoPlayer.value.currentTime = 0
    }
  }
})
</script>

<template>
    <section class="hero-banner" :class="{ 'video-expanded': isExpanded }">
        <div class="hero-content">
            <div class="bailiff-icon"></div>
            <span class="hero-upper-title">{{ upperTitle }}</span>
            <h1 class="hero-title">{{ title }}</h1>
            <OutlineButton
                class="hero-btn"
                :label="buttonLabel"
                @click="openVideo"
                :obfuscated="true"
            />
        </div>
        
            <ClientOnly>
            <div class="hero-videos">
                <video
                    v-if="!isExpanded"
                    class="hero-video hero-video--preview"
                    :class="{ 'is-hidden': isExpanded }"
                    :src="heroFirstVideoSrc"
                    autoplay
                    muted
                    loop
                    playsinline
                    @click="openVideo"
                    preload="true"
                    :poster="heroFirstVideoPoster"
                />
                <video
                    v-else
                    ref="videoPlayer"
                    class="hero-video hero-video--full"
                    :class="{ 'is-visible': isExpanded }"
                    :src="heroSecondVideoSrc"
                    controls
                    playsinline
                    preload="none"
                />
                <button v-if="isExpanded" class="close-btn" @click="closeVideo">&times;</button>
            </div>
            <template #fallback>
                <div class="hero-videos">
                    <img 
                        :src="heroFirstVideoPoster" 
                        alt="Orion App preview"
                        class="hero-video hero-video--preview"
                    />
                </div>
            </template>
        </ClientOnly>
        <div class="background" >
            <img
                src="/images/stars.webp"
                alt="Background Stars"
                loading="eager"
                fetchpriority="high"
            />
        </div>
    </section>
</template>

<style lang="scss" scoped>
.hero-banner {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 125px 0 50px;
    max-width: 1700px;
    margin: 0 auto;
    flex-direction: column;
    gap: 40px;  
    transition: all 0.5s ease-in-out;

    &.video-expanded {
        gap: 0;
        

        .hero-content {
            opacity: 0;
            pointer-events: none;
            width: 0;
            transition-delay:  0s , 0.15s ;
        }

        .hero-videos {
            position: relative;
            top: 100%;
            left: 0;
            width: auto;
            aspect-ratio: 16 / 9;
            height: calc(100vh - 250px);
            max-width: 100%;
            z-index: 100;
            transform: translate(0%, 60px);
            margin: 0 auto;
            transition-delay: 0s, 0.5s, 0s;
        }
    }

    .hero-content {
        text-align: center;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 15px;
        width: 100%;
        max-width: 525px;
        transition: opacity 0.5s ease-in-out, width 0.5s ease-in-out;
        transition-delay:  0.5s , 0s;

        & .bailiff-icon {
            width: 52px;
            height: 38px;
            background-image: url('/images/bailiff-icon.svg');
            background-size: contain;
            background-repeat: no-repeat;
            background-position: center;
            position: relative;

            &::before {
                content: "";
                position: absolute;
                width: 95px;
                aspect-ratio: 1;
                top: -15px;
                left: -10px;
                background-color: rgba(2, 171, 255, 0.5);
                filter: blur(40px);
            }
        }

        & .hero-upper-title {
            text-align: center;
            font-family: $tertiary-font-family;
            font-size: $upper-title-size;
            line-height: $upper-title-line-height;
            background: $title-background-gradient--blue;
            background-clip: text;
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
        }

    
        & .hero-btn {
            margin-top: 20px;
        }
    }

    & .hero-videos {
        width: 100%;
        max-width: 1000px;
        aspect-ratio: 11 / 6;
        display: flex;
        justify-content: center;
        position: relative;
        left: 0;
        transform: translateX(0%);
        transition: all 0.5s ease-in-out, max-width 0.5s ease-in-out, width 0.5s ease-in-out;
        transition-delay: 0.5s, 0s, 0.5s;
        margin-left: auto;

        &:before,
        &::after {
            content: '';
            position: absolute;
            width: 125px;
            aspect-ratio: 1;
            background-image: url(/images/corner-border.webp);
            background-repeat: no-repeat;
            background-size: contain;
            background-position: center;
        }

        &::before {
            top: -15px;
            left: -15px;
        }

        &::after {
            bottom: -15px;
            right: -15px;
            transform: rotate(180deg);
        }
    }

    .hero-video {
        border-radius: 14px;
        box-shadow: 0 4px 24px rgba(0,0,0,0.15);
        cursor: pointer;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: transform 0.5s ease-in-out, opacity 0.5s ease-in-out;

        &--preview {
            position: relative; // Reset position for the preview
            transform: scale(1);
            opacity: 1;

            &.is-hidden {
                transform: scale(0);
                opacity: 0;
            }
        }

        &--full {
            transform: scale(0);
            opacity: 0;

            &.is-visible {
                transform: scale(1);
                opacity: 1;
            }
        }
    }

    .background {
        position: absolute;
        top: 120px;
        aspect-ratio: 1416 / 1078;
        z-index: -1;
        height: 750px;
        left: -25px;
        width: 100vw;
        transform: unset;
        background-position: 0px 90%;
        background-size: 150%;


        &:before,
        &::after {
           content: "";
            position: absolute;
            width: 40vw;
            aspect-ratio: 7 / 6;
            border-radius: 791px;
            opacity: 0.3;
            background: rgba(48, 65, 255, 0.5);
            filter: blur(150px);
        }

        &::before {
            right: 0;
            transform: translate(-50%, -18%);
            top: 0;
            width: 175vw;
        }

        &::after {
            left: 0;
            transform: translate(-45%, -64%);
            top: 50%;
        }
    }
}

.close-btn {
    position: fixed;
    top: 30px;
    right: 30px;
    background: rgba(0,0,0,0.5);
    border: none;
    color: white;
    font-size: 2rem;
    cursor: pointer;
    line-height: 1;
    z-index: 101;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
}


// Responsive
@media (min-width: 767px) {
    .hero-banner {
        flex-direction: row;
        gap: 50px;
        min-height: min(100vh, 850px);
        padding: 75px 25px 0;

        & .hero-upper-title{
            font-size: 28px;
        }

        & .hero-videos:before, 
        & .hero-videos::after{
            width: 175px;
        }
        & .hero-videos{
            &::before {
                top: -35px;
                left: -35px;
            }

            &::after {
                bottom: -35px;
                right: -35px;
            }
        }

        .background{
            width: 100vw;
            height: unset;
            transform: unset;
            left: 50%;
            transform: translate(-50%, 6%);
            background-size: contain;
            background-position: center;
            overflow-x: clip;

            & img{
                right: -19vw;
                position: absolute;
            }

             &::before{
                transform: translate(20%, -19%);
                top: 50%;
                 width: 95vw;
             }
        }
    }
}
</style>
